import {
    FormColorFormField,
    FormTextFormField,
    FormToggleFormField,
    RTEFormField,
    AppPrimaryButton,
} from 'elements'
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
// eslint-disable-next-line no-unused-vars
import { Form, Modal, Checkbox, Popup } from 'semantic-ui-react'
import {
    ResponseTag,
    ResponseTypeSelectionModal,
} from './ResponseTypeSelectionModal'
import { CaretDown, X } from 'phosphor-react'
import { FlexRowBetween, PageHeader } from 'components'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
    CommonUtility,
    FormErrorText,
    TemplateResponseType,
    TemplateQuestionResponseType,
    ChangeAction,
    isAutoNumberResponseItem,
    isNonScorableResponseItem,
    buildAutoNumberDisplaySetting,
    parseAutoNumberConfig,
    isAutoNumberConfigComplete,
    AUTO_NUMBER_DEFAULT_START,
    isFormulaResponseItem,
    isFormulaQuestion,
    isFormulaQuestionCreationEnabled,
    createQuestionGuid,
    readQuestionGuid,
    isQuestionGuid,
    buildFormulaCatalog,
    plainQuestionText,
} from 'utility'
import CreateResponseModal, {
    AddAdditionalInfoModal,
} from './CreateResponseModal'
import styled, { createGlobalStyle } from 'styled-components'
import { GetMultipleResponseTypes } from 'hooks'
import { SetRuleModal } from './SetRuleModal'
import { FormulaEditor } from './FormulaEditor'
import { useAuth } from 'context'

const toQuestionHtml = text => {
    const t = (text || '').trim()
    if (!t) return ''
    return `<p>${t}</p>`
}

const draftFromSavedFormula = (saved, questionId) => {
    if (!saved?.Expr) return null
    const source = saved.Source === 'codes' ? 'codes' : 'excel'
    const text = saved.ExcelText || ''
    return {
        questionId,
        source,
        excelText: source === 'codes' ? '' : text,
        codesText: source === 'codes' ? text : '',
        cellMap: saved.CellMap || {},
        decimals: saved.Decimals == null ? 1 : saved.Decimals,
        suffix: saved.Suffix || '',
        expr: saved.Expr,
        valid: true,
        checking: false,
        error: '',
    }
}

const numericDisplayQuestionId = (setting, question) => {
    const candidates = [
        setting?.QuestionId,
        question?.QuestionDisplaySetting?.QuestionId,
    ]
    const match = candidates.find(value => {
        if (value == null || value === '') return false
        if (isQuestionGuid(value)) return false
        return !String(value).includes('.')
    })
    return match == null ? 0 : match
}

const formulaSummaryText = draft => {
    if (!draft) return ''
    const text = draft.source === 'codes' ? draft.codesText : draft.excelText
    const summary = String(text || draft.expr || '')
        .replace(/\s+/g, ' ')
        .trim()
    if (!summary) return ''
    return summary.length > 72 ? `${summary.slice(0, 69)}...` : summary
}

const getNestedOrderForIndex = index => index + 1

const getNewQuestionFormDefaults = () => ({
    Question: '',
    MinAnswer: 0,
    MaxAnswer: 1,
    IsRequired: false,
    QuestionDisplaySetting: {
        SettingId: 0,
        IsNoteAllowed: true,
        IsMediaAllowed: true,
        QuestionId: 0,
    },
})

const readBooleanOr = (value, fallback) =>
    typeof value === 'boolean' ? value : fallback

const NESTED_EXCLUDED_SINGLE_LABELS = new Set([
    'barcode',
    'parent child',
    'auto number',
    'formula',
])

const nestedAnswerKey = (questionType, responseId) =>
    `${Number(questionType) === TemplateResponseType.Multiple ? TemplateResponseType.Multiple : TemplateResponseType.Single}:${responseId}`

const collectMultipleResponseItems = responseTypes => {
    const rows = []
    const push = item => {
        if (!item || item.GroupId == null) return
        rows.push(item)
    }
    const multipleResponses = responseTypes?.MultipleResponse || []
    const userMultipleResponses = responseTypes?.UserMultipleResponse || []
    const groupedResponses = responseTypes?.GroupedResponse || []
    multipleResponses.forEach(push)
    userMultipleResponses.forEach(push)
    groupedResponses.forEach(group => {
        const groupItems = group?.Items || []
        groupItems.forEach(item =>
            push({
                ...item,
                GroupId: item?.GroupId ?? group.GroupId,
            }),
        )
    })
    const seen = new Set()
    return rows.filter(item => {
        const key = `${item.GroupId}:${item.Id}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

export const buildNestedControlOptions = responseTypes => {
    const multipleOptions = CommonUtility.groupByData(
        collectMultipleResponseItems(responseTypes),
        'GroupId',
    ).map(group => {
        const items = []
        const seenLabels = new Set()
        ;(group.data || []).forEach(item => {
            const text = (item.DisplayText || '').trim()
            const key = text.toLowerCase()
            if (!text || seenLabels.has(key)) return
            seenLabels.add(key)
            items.push(item)
        })
        const label =
            items.map(item => (item.DisplayText || '').trim()).join(' / ') ||
            `Group ${group.title}`
        return {
            key: nestedAnswerKey(TemplateResponseType.Multiple, group.title),
            value: nestedAnswerKey(TemplateResponseType.Multiple, group.title),
            text: label,
            questionType: TemplateResponseType.Multiple,
            responseId: Number(group.title),
            multiResponseType: items[0]?.MultiResponseType,
            optionCount: items.length,
            items,
            groupLabel: 'Multiple choice',
        }
    })

    const singleOptions = (responseTypes?.SingleResponses || [])
        .filter(item => {
            const label = (item?.DisplayText || '').trim().toLowerCase()
            if (NESTED_EXCLUDED_SINGLE_LABELS.has(label)) return false
            if (Number(item?.Id) === 12) return false
            if (isFormulaResponseItem(item)) return false
            return true
        })
        .map(item => ({
            key: nestedAnswerKey(TemplateResponseType.Single, item.Id),
            value: nestedAnswerKey(TemplateResponseType.Single, item.Id),
            text: (item.DisplayText || '').trim(),
            questionType: TemplateResponseType.Single,
            responseId: Number(item.Id),
            multiResponseType: null,
            optionCount: 1,
            items: [],
            groupLabel: 'Other responses',
        }))

    return [...multipleOptions, ...singleOptions]
}

const findNestedControlOption = (options, node) => {
    const questionType =
        Number(node?.QuestionType) === TemplateResponseType.Multiple
            ? TemplateResponseType.Multiple
            : TemplateResponseType.Single
    const responseId = node?.ControlResponseId ?? node?.QuestionResponseId
    return (
        (options || []).find(
            option =>
                option.questionType === questionType &&
                String(option.responseId) === String(responseId),
        ) || null
    )
}

const nestedAnswerLimits = node => {
    const questionType =
        Number(node?.QuestionType) === TemplateResponseType.Multiple
            ? TemplateResponseType.Multiple
            : TemplateResponseType.Single
    if (questionType !== TemplateResponseType.Multiple) {
        return { MinAnswer: 1, MaxAnswer: 1 }
    }
    const count = Number(node.OptionCount) || Number(node.MaxAnswer) || 1
    if (Number(node.MultiResponseType) === TemplateQuestionResponseType.Multiple) {
        const min = node.MinAnswer != null ? Number(node.MinAnswer) : 0
        const max = node.MaxAnswer != null ? Number(node.MaxAnswer) : count
        return { MinAnswer: min, MaxAnswer: Math.max(min, max, 1) }
    }
    return { MinAnswer: 1, MaxAnswer: Math.max(1, count) }
}

const buildNestedChildQuestionPayloads = ({
    nestedNodes,
    familyParentId,
    sectionId,
    sectionName,
    formData,
    isEditingMainQuestion,
}) => {
    if (!Array.isArray(nestedNodes) || nestedNodes.length === 0) return []
    return nestedNodes.map((node, index) => {
        const id = node.Id != null ? node.Id : Math.random()
        const changeAction =
            isEditingMainQuestion && node._persisted
                ? ChangeAction.Update
                : ChangeAction.New
        const questionType =
            Number(node.QuestionType) === TemplateResponseType.Multiple
                ? TemplateResponseType.Multiple
                : TemplateResponseType.Single
        const limits = nestedAnswerLimits(node)
        const source =
            node._source && typeof node._source === 'object' ? { ...node._source } : {}
        delete source.ChildQuestions
        delete source.NestedQuestions
        return {
            ...source,
            Id: id,
            Question: toQuestionHtml(node.Question),
            IsSection: false,
            QuestionType: questionType,
            SectionId: sectionId,
            SectionName: sectionName || source.SectionName || '',
            Order: source.Order,
            QuestionOrder: source.QuestionOrder,
            ParentId:
                familyParentId == null || familyParentId === ''
                    ? familyParentId
                    : String(familyParentId),
            NestedOrder: getNestedOrderForIndex(index),
            IsNestedQuestion: false,
            QuestionResponseId: Number(node.ControlResponseId),
            MultiResponseType:
                questionType === TemplateResponseType.Multiple
                    ? node.MultiResponseType ?? null
                    : null,
            MaxAnswer: limits.MaxAnswer,
            MinAnswer: limits.MinAnswer,
            Score: formData?.Score ?? source.Score ?? 0,
            IsRequired: !!node.IsRequired,
            ColorCode: formData?.ColorCode ?? source.ColorCode,
            QuestionDisplaySetting: {
                ...(source.QuestionDisplaySetting || {}),
                SettingId:
                    source?.QuestionDisplaySetting?.SettingId ||
                    formData?.QuestionDisplaySetting?.SettingId ||
                    0,
                IsNoteAllowed: readBooleanOr(
                    formData?.QuestionDisplaySetting?.IsNoteAllowed,
                    true,
                ),
                IsMediaAllowed: readBooleanOr(
                    formData?.QuestionDisplaySetting?.IsMediaAllowed,
                    true,
                ),
                QuestionId: source?.QuestionDisplaySetting?.QuestionId || 0,
            },
            ParentChildResponse: source.ParentChildResponse || [],
            IsOtherOptionAvailable: !!source.IsOtherOptionAvailable,
            ValidationRule: source.ValidationRule,
            ChangeAction: changeAction,
        }
    })
}

const QuestionSchema = yup.object().shape({
    Question: yup
        .string()
        .trim(FormErrorText.SpacesNotAllowed)
        .strict()
        .required('Question is required'),
    IsRequired: yup.boolean().nullable(),
    ColorCode: yup.string().nullable(),
    Score: yup.number().nullable(),
    MinAnswer: yup
        .number()
        .nullable()
        .typeError('Min Answer must be a valid number')
        .max(
            yup.ref('$maxAnswer'),
            ({ max }) => `Min Answer must be less than or equal to ${max}`,
        )
        .required('*Min Answer is required'),
    MaxAnswer: yup
        .number()
        .nullable()
        .typeError('Max Answer must be a valid number')
        .max(
            yup.ref('$maxAnswer'),
            ({ max }) => `Max Answer must be less than or equal to ${max}`,
        )
        .required('*Max Answer is required'),
    QuestionDisplaySetting: yup.object().shape({
        SettingId: yup.string().nullable(),
        IsNoteAllowed: yup.boolean().nullable(),
        IsMediaAllowed: yup.boolean().nullable(),
        QuestionId: yup.string().nullable(),
        ShowNCQuestion: yup.boolean().nullable(),
    }),
})

const RTEModal = styled.div`
    .ql-picker-options {
        z-index: 99 !important;
    }
`

const NestedModalBody = styled.div`
    min-width: 0;
`

const NestedQuestionModalContent = styled(Modal.Content)`
    &&& {
        overflow-x: hidden !important;
        overflow-y: auto !important;
        flex: 1 1 auto !important;
        max-height: none !important;
    }
`

const NestedIntro = styled.div`
    margin-bottom: 14px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #f8fbff;
    border: 1px solid #dbeafe;
    color: #4b5b75;
    font-size: 14px;
`

const NestedToolbar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    flex-wrap: wrap;
`

const NestList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const NestItem = styled.div`
    padding: 12px;
    border: 1px solid #e2e8f0;
    border-left: 3px solid #60a5fa;
    border-radius: 12px;
    background: #fff;
`

const NestRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 40px;
`

const NestIndex = styled.span`
    width: 18px;
    flex: 0 0 18px;
    color: #64748b;
    font-size: 14px;
    font-weight: 700;
    text-align: right;
`

const NestQuestionInput = styled.input`
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
    height: 40px;
    border: 1px solid #cbd5e1;
    background: #fff;
    color: #0f172a;
    font-size: 15px;
    line-height: 1.4;
    padding: 8px 12px;
    border-radius: 8px;

    &:hover {
        border-color: #94a3b8;
    }

    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
    }

    &::placeholder {
        color: #94a3b8;
    }
`

const NestRequiredField = styled.div`
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    color: #0f172a;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;

    .ui.checkbox {
        min-height: 18px;
    }

    .ui.checkbox label {
        color: #0f172a !important;
        font-size: 13px !important;
        font-weight: 700 !important;
        padding-left: 22px !important;
    }
`

const TypeField = styled.div`
    margin: 8px 0 0 28px;
`

const TypeLabel = styled.div`
    margin-bottom: 4px;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
`

const TypePill = styled.button`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    min-height: 40px;
    border: 1px solid ${props => (props.$open ? '#2563eb' : '#cbd5e1')};
    border-radius: 8px;
    padding: 9px 12px;
    background: #fff;
    color: #0f172a;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
    text-align: left;
    cursor: pointer;
    box-shadow: ${props =>
        props.$open ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none'};

    span {
        flex: 1 1 auto;
        min-width: 0;
        white-space: normal;
        overflow: visible;
        text-overflow: unset;
        word-break: break-word;
    }

    svg {
        flex: 0 0 auto;
        margin-top: 2px;
        color: #64748b;
        transform: ${props => (props.$open ? 'rotate(180deg)' : 'none')};
        transition: transform 0.15s ease;
    }

    &:hover {
        border-color: #94a3b8;
    }
`

const TypeMenu = styled.div`
    width: 100%;
    max-height: 260px;
    overflow: auto;
    margin: 0 0 8px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    padding: 6px;
`

const TypeMenuLabel = styled.div`
    padding: 8px 10px 4px;
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`

const TypeMenuItem = styled.button`
    display: block;
    width: 100%;
    text-align: left;
    border: 0;
    border-radius: 8px;
    background: ${props => (props.$active ? '#eef2ff' : 'transparent')};
    color: ${props => (props.$active ? '#312e81' : '#0f172a')};
    font-weight: ${props => (props.$active ? 700 : 600)};
    font-size: 14px;
    line-height: 1.35;
    white-space: normal;
    padding: 8px 10px;
    cursor: pointer;

    &:hover {
        background: #f8fafc;
    }
`

const NestAddButton = styled.button`
    margin-top: 10px;
    border: 1px dashed #93c5fd;
    background: #f8fbff;
    color: #1d4ed8;
    border-radius: 10px;
    min-height: 40px;
    padding: 8px 14px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        color: #94a3b8;
        cursor: not-allowed;
    }
`

const NestedRemoveButton = styled.button`
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    border: 0;
    background: transparent;
    color: #94a3b8;
    border-radius: 8px;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;

    &:hover {
        background: #fef2f2;
        color: #b91c1c;
    }
`

const NestedModalGlobalStyle = createGlobalStyle`
    .ui.modal.nested-question-config-modal,
    .ui.dimmer.modals.visible .ui.modal.nested-question-config-modal {
        position: fixed !important;
        top: 3vh !important;
        left: 0 !important;
        right: 0 !important;
        bottom: auto !important;
        margin: 0 auto !important;
        width: min(860px, calc(100vw - 32px)) !important;
        height: auto !important;
        max-height: 94vh !important;
        display: flex !important;
        flex-direction: column !important;
        overflow: hidden !important;
        z-index: 3000 !important;
    }
    .ui.modal.nested-question-config-modal > .header {
        flex: 0 0 auto;
        border-bottom: 1px solid #e5e7eb;
    }
    .ui.modal.nested-question-config-modal > .content {
        flex: 1 1 auto;
        min-height: 0;
        overflow-x: hidden !important;
        overflow-y: auto !important;
    }
    .ui.modal.nested-question-config-modal > .actions {
        flex: 0 0 auto;
        border-top: 1px solid #e5e7eb;
        background: #fff;
    }
    @media (max-width: 768px) {
        .ui.modal.nested-question-config-modal,
        .ui.dimmer.modals.visible .ui.modal.nested-question-config-modal {
            top: 0 !important;
            width: 100vw !important;
            max-height: 100vh !important;
            height: 100vh !important;
            border-radius: 0 !important;
        }
        .ui.modal.nested-question-config-modal > .header,
        .ui.modal.nested-question-config-modal > .content,
        .ui.modal.nested-question-config-modal > .actions {
            padding-left: 14px !important;
            padding-right: 14px !important;
        }
        .nested-modal-actions {
            flex-direction: column !important;
            align-items: stretch !important;
        }
        .nested-modal-actions .nested-modal-action-buttons,
        .nested-modal-actions .ui.button {
            width: 100%;
        }
    }
`

const createNestedNode = defaultOption => ({
    Id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    Question: '',
    ControlResponseId: defaultOption?.responseId ?? null,
    QuestionType: defaultOption?.questionType ?? TemplateResponseType.Single,
    MultiResponseType: defaultOption?.multiResponseType ?? null,
    OptionCount: defaultOption?.optionCount ?? 1,
    IsRequired: false,
})

const removeNodeByIdFlat = (nodes, nodeId) =>
    nodes.filter(node => node.Id !== nodeId)

const updateNodeByIdFlat = (nodes, nodeId, updater) =>
    nodes.map(node => (node.Id === nodeId ? updater(node) : node))

const NestedQuestionConfigModal = ({
    open,
    onDismiss,
    value = [],
    onSave,
    controlTypeOptions = [],
    parentQuestionText = '',
    parentResponseLabel = '',
    onCreateCustomGroup,
}) => {
    const [nodes, setNodes] = useState([])
    const [formError, setFormError] = useState('')
    const [openPickerId, setOpenPickerId] = useState(null)
    const wasOpenRef = useRef(false)

    const defaultOption =
        controlTypeOptions.find(
            option => option.questionType === TemplateResponseType.Single,
        ) || controlTypeOptions[0]

    const multipleOptions = controlTypeOptions.filter(
        option => option.groupLabel === 'Multiple choice',
    )
    const singleOptions = controlTypeOptions.filter(
        option => option.groupLabel === 'Other responses',
    )

    useEffect(() => {
        if (open && !wasOpenRef.current) {
            const raw = Array.isArray(value) ? value : []
            const normalized = raw.map(n => {
                const match = findNestedControlOption(controlTypeOptions, n)
                const fallback = match || defaultOption
                return {
                    ...n,
                    QuestionType:
                        match?.questionType ||
                        n.QuestionType ||
                        fallback?.questionType ||
                        TemplateResponseType.Single,
                    ControlResponseId:
                        match?.responseId ??
                        n.ControlResponseId ??
                        fallback?.responseId ??
                        null,
                    MultiResponseType:
                        match?.multiResponseType ?? n.MultiResponseType ?? null,
                    OptionCount: match?.optionCount ?? n.OptionCount ?? 1,
                    IsRequired: !!n.IsRequired,
                }
            })
            setNodes(normalized)
            setFormError('')
            setOpenPickerId(null)
        }
        wasOpenRef.current = open
    }, [open, value, controlTypeOptions, defaultOption])

    const addNestedQuestion = () => {
        setFormError('')
        setNodes(prev => [...prev, createNestedNode(defaultOption)])
    }

    const updateNodeField = (nodeId, field, fieldValue) => {
        setNodes(prev =>
            updateNodeByIdFlat(prev, nodeId, node => ({
                ...node,
                [field]: fieldValue,
            })),
        )
    }

    const applyAnswerType = (nodeId, optionValue) => {
        const option = controlTypeOptions.find(item => item.value === optionValue)
        if (!option) return
        setNodes(prev =>
            updateNodeByIdFlat(prev, nodeId, node => ({
                ...node,
                QuestionType: option.questionType,
                ControlResponseId: option.responseId,
                MultiResponseType: option.multiResponseType,
                OptionCount: option.optionCount,
                MinAnswer:
                    option.questionType === TemplateResponseType.Multiple &&
                    Number(option.multiResponseType) ===
                        TemplateQuestionResponseType.Multiple
                        ? 0
                        : 1,
                MaxAnswer: option.optionCount || 1,
            })),
        )
    }

    const deleteNode = nodeId => {
        setNodes(prev => removeNodeByIdFlat(prev, nodeId))
    }

    const saveNodes = () => {
        const incomplete = nodes.find(
            node =>
                !(node.Question || '').trim() ||
                node.ControlResponseId == null ||
                node.ControlResponseId === '',
        )
        if (incomplete) {
            setFormError('Each nested question needs text and an answer type.')
            return
        }
        setFormError('')
        onSave(nodes)
    }

    return (
        <Modal
            open={open}
            onClose={() => onDismiss()}
            size="large"
            className="nested-question-config-modal"
        >
            <NestedModalGlobalStyle />
            <Modal.Header>
                <FlexRowBetween className="align-items-start">
                    <div style={{ minWidth: 0, paddingRight: 12 }}>
                        <PageHeader header="Nested Questions" />
                        <div
                            style={{
                                marginTop: 6,
                                fontSize: 14,
                                color: '#64748b',
                                lineHeight: 1.4,
                                wordBreak: 'break-word',
                            }}
                        >
                            <div>
                                <strong>Question:</strong> {parentQuestionText || '—'}
                            </div>
                            <div>
                                <strong>Response:</strong> {parentResponseLabel || '—'}
                            </div>
                        </div>
                    </div>
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onDismiss()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <NestedQuestionModalContent>
                <NestedModalBody>
                    <NestedIntro>
                        Add follow-up questions. Each one can be Yes / No / N/A, Pass / Fail / N/A, a custom scored group, or a single answer. Tick Required when it must be answered.
                    </NestedIntro>
                    {formError && (
                        <div
                            style={{
                                marginBottom: 12,
                                padding: '8px 12px',
                                borderRadius: 8,
                                background: '#fef2f2',
                                color: '#b91c1c',
                                fontWeight: 600,
                            }}
                        >
                            {formError}
                        </div>
                    )}

                    <NestedToolbar>
                        <div style={{ color: '#64748b', fontWeight: 700 }}>
                            {nodes.length} question{nodes.length !== 1 ? 's' : ''}
                        </div>
                        {typeof onCreateCustomGroup === 'function' && (
                            <button
                                type="button"
                                onClick={onCreateCustomGroup}
                                style={{
                                    border: 0,
                                    background: 'transparent',
                                    color: '#0f766e',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                }}
                            >
                                + Custom group
                            </button>
                        )}
                    </NestedToolbar>

                    <NestList>
                        {nodes.map((node, index) => {
                            const selected = findNestedControlOption(
                                controlTypeOptions,
                                node,
                            )
                            const pickerOpen = openPickerId === node.Id
                            return (
                                <NestItem key={node.Id}>
                                <NestRow>
                                    <NestIndex>{index + 1}</NestIndex>
                                    <NestQuestionInput
                                        placeholder="Enter question"
                                        value={node.Question}
                                        onChange={e =>
                                            updateNodeField(
                                                node.Id,
                                                'Question',
                                                e.target.value,
                                            )
                                        }
                                    />
                                    <NestRequiredField>
                                        <Checkbox
                                            label="Required"
                                            checked={!!node.IsRequired}
                                            onChange={(_, data) =>
                                                updateNodeField(
                                                    node.Id,
                                                    'IsRequired',
                                                    !!data.checked,
                                                )
                                            }
                                        />
                                    </NestRequiredField>
                                    <NestedRemoveButton
                                        type="button"
                                        onClick={() => deleteNode(node.Id)}
                                        aria-label="Remove question"
                                    >
                                        ×
                                    </NestedRemoveButton>
                                </NestRow>
                                <TypeField>
                                    <TypeLabel>Answer type</TypeLabel>
                                    <TypePill
                                        type="button"
                                        $open={pickerOpen}
                                        aria-haspopup="listbox"
                                        aria-expanded={pickerOpen}
                                        title={selected?.text || 'Answer type'}
                                        onClick={() =>
                                            setOpenPickerId(current =>
                                                current === node.Id ? null : node.Id,
                                            )
                                        }
                                    >
                                        <span>{selected?.text || 'Answer type'}</span>
                                        <CaretDown size={14} weight="bold" />
                                    </TypePill>
                                    {pickerOpen && (
                                        <TypeMenu>
                                            {multipleOptions.length > 0 && (
                                                <>
                                                    <TypeMenuLabel>Multiple choice</TypeMenuLabel>
                                                    {multipleOptions.map(option => (
                                                        <TypeMenuItem
                                                            key={option.value}
                                                            type="button"
                                                            $active={selected?.value === option.value}
                                                            onClick={() => {
                                                                applyAnswerType(node.Id, option.value)
                                                                setOpenPickerId(null)
                                                            }}
                                                        >
                                                            {option.text}
                                                        </TypeMenuItem>
                                                    ))}
                                                </>
                                            )}
                                            {singleOptions.length > 0 && (
                                                <>
                                                    <TypeMenuLabel>Other responses</TypeMenuLabel>
                                                    {singleOptions.map(option => (
                                                        <TypeMenuItem
                                                            key={option.value}
                                                            type="button"
                                                            $active={selected?.value === option.value}
                                                            onClick={() => {
                                                                applyAnswerType(node.Id, option.value)
                                                                setOpenPickerId(null)
                                                            }}
                                                        >
                                                            {option.text}
                                                        </TypeMenuItem>
                                                    ))}
                                                </>
                                            )}
                                            {typeof onCreateCustomGroup === 'function' && (
                                                <TypeMenuItem
                                                    type="button"
                                                    onClick={() => {
                                                        setOpenPickerId(null)
                                                        onCreateCustomGroup()
                                                    }}
                                                >
                                                    + New custom group
                                                </TypeMenuItem>
                                            )}
                                        </TypeMenu>
                                    )}
                                </TypeField>
                                </NestItem>
                            )
                        })}
                        {!nodes.length && (
                            <div
                                style={{
                                    padding: '12px 0 4px',
                                    color: '#64748b',
                                    fontWeight: 600,
                                }}
                            >
                                No follow-up questions yet.
                            </div>
                        )}
                        <NestAddButton
                            type="button"
                            onClick={addNestedQuestion}
                            disabled={!controlTypeOptions.length}
                        >
                            + Add question to this nest
                        </NestAddButton>
                    </NestList>
                </NestedModalBody>
            </NestedQuestionModalContent>
            <Modal.Actions
                className="nested-modal-actions"
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ color: '#64748b', fontWeight: 600 }}>
                    Total nested items: {nodes.length}
                </div>
                <div
                    className="d-flex flex-wrap nested-modal-action-buttons"
                    style={{ gap: 10 }}
                >
                    <AppPrimaryButton
                        type="button"
                        onClick={saveNodes}
                        style={{ backgroundColor: '#1d4ed8', borderColor: '#1d4ed8' }}
                    >
                        Save Nested Questions
                    </AppPrimaryButton>
                    <AppPrimaryButton
                        type="button"
                        onClick={() => onDismiss()}
                        style={{ backgroundColor: '#6b7280', borderColor: '#6b7280' }}
                    >
                        Close
                    </AppPrimaryButton>
                </div>
            </Modal.Actions>
        </Modal>
    )
}

// Create a global variable outside the component
// const globalLatestParentChildData = null

export const AddEditQuestionModal = ({
    open,
    onClose,
    question,
    editMode,
    sectionId,
    sectionName = '',
    responseTypes,
    setResponseTypes,
    templateId,
    templateNumericId,
    onNext, // New prop for Next functionality
    isFirstQuestion = false, // New prop to indicate if this is the first question
    lastResponseType = null, // New prop to pass the last selected response type
    onResponseTypeChange, // New callback to pass responseType back to parent
    /** Shared ParentId for this main + nested rows (same pattern as SectionId: Math.random()). */
    nestedFamilyParentId = null,
    sectionQuestions = [],
    templateSections = [],
    onStampQuestionGuids,
}) => {
    console.log(
        'AddEditQuestionModal - Received templateId:',
        templateId,
        templateNumericId,
    )
    const { user } = useAuth()
    const [responseType, setResponseType] = useState(null)
    const [customCheckbox, setCustomCheckbox] = useState(false)
    const [isNestedModalOpen, setIsNestedModalOpen] = useState(false)
    const [nestedQuestions, setNestedQuestions] = useState([])
    const [parentChildResponse, setParentChildResponse] = useState([])
    const [isOtherOptionAvailable, setIsOtherOptionAvailable] = useState(false)
    const [autoNumberConfig, setAutoNumberConfig] = useState({
        AutoNumberType: 0,
        AutoNumberPreFix: '',
        AutoNumberStartFrom: AUTO_NUMBER_DEFAULT_START,
    })

    // Add this useRef to store the latest data
    const latestParentChildRef = useRef([])
    const latestAutoNumberRef = useRef({
        AutoNumberType: 0,
        AutoNumberPreFix: '',
        AutoNumberStartFrom: AUTO_NUMBER_DEFAULT_START,
    })
    const formulaDraftRef = useRef(null)
    const formulaSnapshotRef = useRef(null)
    const guidCacheRef = useRef(new Map())
    const [questionGuid, setQuestionGuid] = useState('')
    const [formulaModalOpen, setFormulaModalOpen] = useState(false)
    const [formulaConfig, setFormulaConfig] = useState(null)
    const [formulaSummary, setFormulaSummary] = useState('')
    const [formulaChecking, setFormulaChecking] = useState(false)
    const [formulaNotice, setFormulaNotice] = useState('')
    const formulaEnabled = isFormulaQuestionCreationEnabled(
        user?.CompanyId || user?.Companies?.[0]?.CompanyId,
        user?.Capabilities,
    )

    const maxAnswer = useMemo(
        () => responseType?.item?.Items?.length || 1,
        [responseType],
    )
    const {
        control,
        handleSubmit,
        setError,
        formState: { errors },
        reset,
        watch,
        setValue,
    } = useForm({
        resolver: yupResolver(QuestionSchema),
        context: {
            maxAnswer,
        },
        defaultValues: getNewQuestionFormDefaults(),
    })
    const [listBoxLimitsByGroupId, setListBoxLimitsByGroupId] = useState({})
    const [openModal, setOpenModal] = useState(false)
    const [isRuleModalOpen, setIsRuleModalOpen] = useState(false)
    const [validationRule, setValidationRule] = useState(null)
    const [createModal, setCreateModal] = useState(false)
    const [additionalInfoModal, setAdditionalInfoModal] = useState(false)
    const [additionalInfoModalData, setAdditionalInfoModalData] = useState({})
    const { data: multipleResponcesList } = GetMultipleResponseTypes()
    useEffect(() => {
        setResponseType(null)
        setValidationRule(null)
        if (open && editMode && question?.Id) {
            reset(question)
            // Explicitly sync nested toggle values into RHF state.
            // This avoids cases where nested fields stay undefined after modal reopen.
            setValue(
                'QuestionDisplaySetting.IsNoteAllowed',
                readBooleanOr(
                    question?.QuestionDisplaySetting?.IsNoteAllowed,
                    true,
                ),
                { shouldDirty: false },
            )
            setValue(
                'QuestionDisplaySetting.IsMediaAllowed',
                readBooleanOr(
                    question?.QuestionDisplaySetting?.IsMediaAllowed,
                    true,
                ),
                { shouldDirty: false },
            )
            if (question.QuestionType === TemplateResponseType.Multiple) {
                const groupedItem = responseTypes.GroupedResponse.find(
                    x => +x.GroupId === question.QuestionResponseId,
                )
                setResponseType({
                    type: question.QuestionType,
                    item: groupedItem,
                })
                if (
                    groupedItem?.Items?.[0]?.MultiResponseType ===
                        TemplateQuestionResponseType.Multiple &&
                    question.MinAnswer != null &&
                    question.MaxAnswer != null
                ) {
                    setListBoxLimitsByGroupId(prev => ({
                        ...prev,
                        [groupedItem.GroupId]: {
                            MinAnswer: Number(question.MinAnswer),
                            MaxAnswer: Number(question.MaxAnswer),
                        },
                    }))
                }
            } else {
                setResponseType({
                    type: question.QuestionType,
                    item: responseTypes.SingleResponses.find(
                        x => x.Id === question.QuestionResponseId,
                    ),
                })
            }
            setIsOtherOptionAvailable(question.IsOtherOptionAvailable || false)
            const existingAuto = parseAutoNumberConfig({
                ...question,
                QuestionDisplaySetting: question?.QuestionDisplaySetting,
            })
            latestAutoNumberRef.current = existingAuto
            setAutoNumberConfig(existingAuto)
            const existingNested = Array.isArray(question?.NestedQuestions)
                ? question.NestedQuestions.map(n => ({
                    ...n,
                    _persisted: true,
                }))
                : []
            setNestedQuestions(existingNested)
            setCustomCheckbox(
                question?.IsNestedQuestion === true ||
                    existingNested.length > 0,
            )
            // Don't set default for edit mode
        } else if (open && !editMode) {
            reset(getNewQuestionFormDefaults())
            setValue('QuestionDisplaySetting.IsNoteAllowed', true, {
                shouldDirty: false,
            })
            setValue('QuestionDisplaySetting.IsMediaAllowed', true, {
                shouldDirty: false,
            })
            // For first question, don't set any default
            // For subsequent questions, use the last selected response type
            if (isFirstQuestion) {
                // Don't set any default for first question
                setResponseType(null)
            } else if (
                lastResponseType &&
                (formulaEnabled || !isFormulaResponseItem(lastResponseType.item))
            ) {
                // Use the last selected response type for subsequent questions
                setResponseType(lastResponseType)
            } else {
                // Fallback: if no lastResponseType is available, don't set default
                setResponseType(null)
            }
            setCustomCheckbox(false)
            setNestedQuestions([])
            latestAutoNumberRef.current = {
                AutoNumberType: 0,
                AutoNumberPreFix: '',
                AutoNumberStartFrom: AUTO_NUMBER_DEFAULT_START,
            }
            setAutoNumberConfig({
                AutoNumberType: 0,
                AutoNumberPreFix: '',
                AutoNumberStartFrom: AUTO_NUMBER_DEFAULT_START,
            })
        }
    }, [open, editMode, question, isFirstQuestion, lastResponseType, formulaEnabled])

    useEffect(() => {
        if (!open) {
            setQuestionGuid('')
            formulaDraftRef.current = null
            return
        }
        const existing = readQuestionGuid(question)
        const guid = existing || createQuestionGuid()
        const seeded = draftFromSavedFormula(question?.Formula, guid)
        setQuestionGuid(guid)
        formulaDraftRef.current = seeded
        formulaSnapshotRef.current = seeded
        setFormulaConfig(question?.Formula || null)
        setFormulaSummary(
            formulaSummaryText(seeded) ||
                question?.Formula?.ExcelText ||
                question?.Formula?.Expr ||
                '',
        )
        setFormulaChecking(false)
        setFormulaNotice('')
        setFormulaModalOpen(false)
    }, [open, question?.Id])

    useEffect(() => {
        const displayText = (responseType?.item?.DisplayText || '').trim()
        const isEligible =
            responseType?.type === TemplateResponseType.Single &&
            (displayText === 'Number' || displayText === 'Date')
        if (!isEligible && validationRule) {
            setValidationRule(null)
        }
    }, [responseType])
    useEffect(() => {
        if (responseType?.type === TemplateResponseType.Multiple) {
            setCustomCheckbox(false)
            setNestedQuestions([])
            setIsNestedModalOpen(false)
        }
    }, [responseType?.type])
    useEffect(() => {
        console.log('Updated ParentChildResponse:', parentChildResponse)
    }, [parentChildResponse])

    useEffect(() => {
        console.log('isNestedModalOpen:', isNestedModalOpen) // Debugging log
    }, [isNestedModalOpen])

    useEffect(() => {
        if (editMode && question?.ParentChildResponse?.length) {
            console.log(
                'Initializing parentChildResponse with:',
                question.ParentChildResponse,
            )
            latestParentChildRef.current = question.ParentChildResponse
            setParentChildResponse(question.ParentChildResponse)
        }
    }, [editMode, question])

    const handleAutoNumberDataReceived = data => {
        if (data) {
            latestAutoNumberRef.current = data
            setAutoNumberConfig(data)
        }
    }

    const handleParentChildDataReceived = data => {
        console.log('Parent-child data received in AddEditQuestion:', data)
        if (data) {
            // Support both wrapped payload ({ ParentChildResponse, IsOtherOptionAvailable })
            // and legacy array payloads.
            let rows = []
            if (Array.isArray(data.ParentChildResponse)) {
                rows = data.ParentChildResponse
            } else if (Array.isArray(data)) {
                rows = data
            }

            // Set the latest data in the ref immediately
            latestParentChildRef.current = data
            setParentChildResponse(rows)

            if (typeof data.IsOtherOptionAvailable === 'boolean') {
                setIsOtherOptionAvailable(data.IsOtherOptionAvailable)
            }
        }
    }

    const submit = async formData => {
        if (CommonUtility.strippedHtml(formData.Question).trim().length <= 0) {
            setError('Question', {
                type: 'custom',
                message: 'Question is required',
            })
            return
        }

        if (!responseType) {
            setError('responseType', {
                type: 'manual',
                message: 'Please select a response type',
            })
            return
        }

        if (isAutoNumberResponseItem(responseType?.item)) {
            const cfg =
                latestAutoNumberRef.current || autoNumberConfig || {}
            if (!isAutoNumberConfigComplete(cfg)) {
                setError('responseType', {
                    type: 'custom',
                    message:
                        'Please configure Auto Number (type, Start from, and PreFix if Alpha numeric).',
                })
                return
            }
        }

        if (rejectFormulaIfNeeded()) return

        // Use the window global variable if it exists
        const rawParentChildData =
            window.parentChildModalData ||
            latestParentChildRef.current ||
            parentChildResponse

        console.log(
            'Using data from window global:',
            window.parentChildModalData,
        )
        console.log('Using data for submission:', rawParentChildData)

        let parentChildRows = []
        if (Array.isArray(rawParentChildData?.ParentChildResponse)) {
            parentChildRows = rawParentChildData.ParentChildResponse
        } else if (Array.isArray(rawParentChildData)) {
            parentChildRows = rawParentChildData
        }

        if (parentChildRows.length) {
            parentChildRows.forEach(parent => {
                console.log(
                    `Before finalizing: Parent ${parent.ParentName} has ChildLabel: ${parent.ChildLabel}`,
                )
            })
        }

        const finalParentChildResponse = parentChildRows.map(parent => ({
            ...parent,
            ChildLabel: parent.ChildLabel, // Don't trim, just use as-is
            Children: parent.Children?.map(child => ({
                ...child,
                ChildLabel: parent.ChildLabel,
            })),
        }))

        // Derive "Allow Other Response" from either existing question state
        // or any ParentChildResponse row which has IsOtherOptionAvailable = true.
        const finalIsOtherOptionAvailable =
            typeof rawParentChildData?.IsOtherOptionAvailable === 'boolean'
                ? rawParentChildData.IsOtherOptionAvailable
                : isOtherOptionAvailable

        // One final verification
        finalParentChildResponse.forEach(parent => {
            console.log(
                `Final: Parent ${parent.ParentName} has ChildLabel: ${parent.ChildLabel}`,
            )
        })

        // Determine ChangeAction based on create vs update
        const isExisting = !!question?.Id
        const changeActionPayload = { ChangeAction: isExisting ? ChangeAction.Update : ChangeAction.New }

        const mainQuestionId = question?.Id || Math.random()
        const hasNested = customCheckbox && nestedQuestions.length > 0
        let familyParentId =
            question?.ParentId != null && question?.ParentId !== ''
                ? question.ParentId
                : nestedFamilyParentId
        if (hasNested && (familyParentId == null || familyParentId === '')) {
            familyParentId = Math.random()
        }
        if (familyParentId != null && familyParentId !== '') {
            familyParentId = String(familyParentId)
        }
        const isEditingMainQuestion = !!(editMode && question?.Id)
        const childQuestionPayloads = hasNested
            ? buildNestedChildQuestionPayloads({
                nestedNodes: nestedQuestions,
                familyParentId,
                sectionId,
                sectionName,
                formData,
                isEditingMainQuestion,
            })
            : []

        const isAutoNumberQ = isAutoNumberResponseItem(responseType?.item)
        const autoNumberCfg = isAutoNumberQ
            ? latestAutoNumberRef.current || autoNumberConfig || {}
            : null

        const noteAllowed = readBooleanOr(
            formData?.QuestionDisplaySetting?.IsNoteAllowed,
            readBooleanOr(question?.QuestionDisplaySetting?.IsNoteAllowed, true),
        )
        const mediaAllowed = readBooleanOr(
            formData?.QuestionDisplaySetting?.IsMediaAllowed,
            readBooleanOr(question?.QuestionDisplaySetting?.IsMediaAllowed, true),
        )

        const temp = {
            ...(question || {}),
            ...formData,
            Score: isNonScorableResponseItem(responseType?.item)
                ? 0
                : formData?.Score ?? 0,
            IsSection: false,
            QuestionType: responseType.type,
            SectionId: sectionId,
            Id: mainQuestionId,
            MaxAnswer: formData.MaxAnswer,
            MinAnswer: formData.MinAnswer,
            QuestionResponseId:
                responseType.type === TemplateResponseType.Multiple
                    ? +responseType.item.GroupId
                    : responseType.item.Id,
            QuestionDisplaySetting: buildAutoNumberDisplaySetting(
                {
                    SettingId:
                        formData?.QuestionDisplaySetting?.SettingId ||
                        question?.QuestionDisplaySetting?.SettingId ||
                        0,
                    IsNoteAllowed: noteAllowed,
                    IsMediaAllowed: mediaAllowed,
                    QuestionId: formData?.QuestionId || question?.Id || 0,
                },
                isAutoNumberQ ? autoNumberCfg : null,
            ),
            ...additionalInfoModalData,
            ParentChildResponse: finalParentChildResponse,
            // For Multiple Responses we take IsOtherOptionAvailable from the
            // response modal; for Parent-Child we mirror it from any parent row.
            IsOtherOptionAvailable: finalIsOtherOptionAvailable,
            ValidationRule: validationRule || undefined,
            ShowNCQuestion: formData?.ShowNCQuestion,
            IsNestedQuestion: !!hasNested,
            NestedOrder: hasNested ? 0 : question?.NestedOrder || 0,
            ParentId: hasNested ? familyParentId : '',
            ChildQuestions: childQuestionPayloads,
            ...changeActionPayload,
        }

        console.log('Final data being sent to API:', temp)
        const questionPayload = withFormulaIdentity(temp)

        // Pass responseType back to parent for next question
        if (onResponseTypeChange && responseType) {
            onResponseTypeChange(responseType)
        }

        // Clear the global variable
        window.parentChildModalData = null

        onClose(questionPayload)
    }

    const handleNext = async formData => {
        // Same logic as submit but call onNext instead of onClose
        if (!responseType) {
            setError('responseType', {
                type: 'manual',
                message: 'Please select a response type',
            })
            return
        }

        if (isAutoNumberResponseItem(responseType?.item)) {
            const cfg =
                latestAutoNumberRef.current || autoNumberConfig || {}
            if (!isAutoNumberConfigComplete(cfg)) {
                setError('responseType', {
                    type: 'custom',
                    message:
                        'Please configure Auto Number (type, Start from, and PreFix if Alpha numeric).',
                })
                return
            }
        }

        if (rejectFormulaIfNeeded()) return

        const finalParentChildResponse = parentChildResponse.map(parent => ({
            ...parent,
            Children: parent.Children.map(child => ({
                ...child,
                ChildLabel: parent.ChildLabel,
            })),
        }))

        finalParentChildResponse.forEach(parent => {
            console.log(
                `Final: Parent ${parent.ParentName} has ChildLabel: ${parent.ChildLabel}`,
            )
        })

        const isExisting = !!question?.Id
        const changeActionPayload = { ChangeAction: isExisting ? ChangeAction.Update : ChangeAction.New }

        const mainQuestionId = question?.Id || Math.random()
        const hasNested = customCheckbox && nestedQuestions.length > 0
        let familyParentId =
            question?.ParentId != null && question?.ParentId !== ''
                ? question.ParentId
                : nestedFamilyParentId
        if (hasNested && (familyParentId == null || familyParentId === '')) {
            familyParentId = Math.random()
        }
        if (familyParentId != null && familyParentId !== '') {
            familyParentId = String(familyParentId)
        }
        const isEditingMainQuestion = !!(editMode && question?.Id)
        const childQuestionPayloads = hasNested
            ? buildNestedChildQuestionPayloads({
                nestedNodes: nestedQuestions,
                familyParentId,
                sectionId,
                sectionName,
                formData,
                isEditingMainQuestion,
            })
            : []

        const isAutoNumberQNext = isAutoNumberResponseItem(responseType?.item)
        const autoNumberCfgNext = isAutoNumberQNext
            ? latestAutoNumberRef.current || autoNumberConfig || {}
            : null

        const noteAllowed = readBooleanOr(
            formData?.QuestionDisplaySetting?.IsNoteAllowed,
            readBooleanOr(question?.QuestionDisplaySetting?.IsNoteAllowed, true),
        )
        const mediaAllowed = readBooleanOr(
            formData?.QuestionDisplaySetting?.IsMediaAllowed,
            readBooleanOr(question?.QuestionDisplaySetting?.IsMediaAllowed, true),
        )

        const temp = {
            ...(question || {}),
            ...formData,
            Score: isNonScorableResponseItem(responseType?.item)
                ? 0
                : formData?.Score ?? 0,
            IsSection: false,
            QuestionType: responseType.type,
            SectionId: sectionId,
            Id: mainQuestionId,
            MaxAnswer: formData.MaxAnswer,
            MinAnswer: formData.MinAnswer,
            QuestionResponseId:
                responseType.type === TemplateResponseType.Multiple
                    ? +responseType.item.GroupId
                    : responseType.item.Id,
            QuestionDisplaySetting: buildAutoNumberDisplaySetting(
                {
                    SettingId:
                        formData?.QuestionDisplaySetting?.SettingId ||
                        question?.QuestionDisplaySetting?.SettingId ||
                        0,
                    IsNoteAllowed: noteAllowed,
                    IsMediaAllowed: mediaAllowed,
                    QuestionId: formData?.QuestionId || question?.Id || 0,
                },
                isAutoNumberQNext ? autoNumberCfgNext : null,
            ),
            ...additionalInfoModalData,
            ParentChildResponse: finalParentChildResponse,
            IsOtherOptionAvailable: isOtherOptionAvailable,
            ValidationRule: validationRule || undefined,
            ShowNCQuestion: formData?.ShowNCQuestion,
            IsNestedQuestion: !!hasNested,
            NestedOrder: hasNested ? 0 : question?.NestedOrder || 0,
            ParentId: hasNested ? familyParentId : '',
            ChildQuestions: childQuestionPayloads,
            ...changeActionPayload,
        }

        console.log('Final data being sent for Next:', temp)
        const questionPayload = withFormulaIdentity(temp)
        window.parentChildModalData = null

        // Pass responseType back to parent for next question
        if (onResponseTypeChange && responseType) {
            onResponseTypeChange(responseType)
        }

        // Reset form and state for next question
        reset(getNewQuestionFormDefaults())
        setAdditionalInfoModalData({})
        setParentChildResponse([])
        setIsOtherOptionAvailable(false)
        latestAutoNumberRef.current = {
            AutoNumberType: 0,
            AutoNumberPreFix: '',
            AutoNumberStartFrom: AUTO_NUMBER_DEFAULT_START,
        }
        setAutoNumberConfig({
            AutoNumberType: 0,
            AutoNumberPreFix: '',
            AutoNumberStartFrom: AUTO_NUMBER_DEFAULT_START,
        })
        setNestedQuestions([])
        setCustomCheckbox(false)
        setIsNestedModalOpen(false)
        formulaDraftRef.current = null
        formulaSnapshotRef.current = null
        setFormulaConfig(null)
        setFormulaSummary('')
        setFormulaChecking(false)
        setFormulaNotice('')
        setFormulaModalOpen(false)
        setQuestionGuid(createQuestionGuid())

        // Don't set default response type here - it will be set by useEffect based on lastResponseType prop

        if (onNext) {
            onNext(questionPayload)
        } else {
            onClose(questionPayload)
        }
    }

    const applyMinMaxToQuestionForm = ({ MinAnswer, MaxAnswer }) => {
        if (MinAnswer != null) {
            setValue('MinAnswer', Number(MinAnswer))
        }
        if (MaxAnswer != null) {
            setValue('MaxAnswer', Number(MaxAnswer))
        }
        if (Number(MinAnswer) >= 1) {
            setValue('IsRequired', true)
        }
    }

    const handleMinMaxChange = ({ MinAnswer, MaxAnswer }) => {
        applyMinMaxToQuestionForm({ MinAnswer, MaxAnswer })
    }

    const handleResponseTypeClose = item => {
        if (item) {
            setResponseType(item)
            if (item.type === TemplateResponseType.Multiple && item.item?.GroupId) {
                const groupId = item.item.GroupId
                const multiType = item.item.Items?.[0]?.MultiResponseType
                const stored = listBoxLimitsByGroupId[groupId]
                if (
                    multiType === TemplateQuestionResponseType.Multiple &&
                    stored
                ) {
                    applyMinMaxToQuestionForm(stored)
                } else if (multiType === TemplateQuestionResponseType.Single) {
                    setValue('MinAnswer', 1)
                    setValue('MaxAnswer', item.item.Items?.length || 1)
                }
            }
        }
        if (
            item?.type === TemplateResponseType.Single &&
            isFormulaResponseItem(item.item)
        ) {
            formulaSnapshotRef.current = formulaDraftRef.current
            setFormulaModalOpen(true)
        }
        setOpenModal(false)
    }

    const onCreteClose = data => {
        if (data) {
            const groupedResponse = CommonUtility.groupByData(
                data.Items,
                'GroupId',
            ).map(item => ({
                GroupId: item.title,
                Items: item.data,
            }))
            const newResponse = {
                ...responseTypes,
                GroupedResponse: groupedResponse,
                UserMultipleResponse: [...data.Items],
            }
            setResponseTypes(newResponse)
            if (typeof data.IsOtherOptionAvailable !== 'undefined') {
                setIsOtherOptionAvailable(data.IsOtherOptionAvailable)
            }
            if (
                data.MinAnswer != null &&
                data.MaxAnswer != null &&
                data.Items?.[0]?.GroupId != null
            ) {
                const groupId = data.Items[0].GroupId
                setListBoxLimitsByGroupId(prev => ({
                    ...prev,
                    [groupId]: {
                        MinAnswer: Number(data.MinAnswer),
                        MaxAnswer: Number(data.MaxAnswer),
                    },
                }))
                applyMinMaxToQuestionForm({
                    MinAnswer: data.MinAnswer,
                    MaxAnswer: data.MaxAnswer,
                })
            }
        }
        setCreateModal(false)
    }

    const onAdditionalInfoClose = formData => {
        setAdditionalInfoModalData(formData)
        setAdditionalInfoModal(false)
    }

    const onpenAdditionalInfoClose = () => {
        setAdditionalInfoModalData({
            AdditionalInfo: question?.AdditionalInfo,
            AddtionalInfoImage: question?.AddtionalInfoImage,
            IsShowFullAddtionalInfo: question?.IsShowFullAddtionalInfo,
            IsHint: question?.IsHint,
        })
        setAdditionalInfoModal(true)
    }
    const [questionScore, setQuestionScore] = useState(0) // State to hold the entered score

    const handleScoreChange = value => {
        setQuestionScore(value) // Update state on score input change
    }

    const handleCreateMultipleResponse = () => {
        setCreateModal({})
    }

    const handleEditMultipleResponse = item => {
        setCreateModal(item)
    }

    const isScoreDisabled = useMemo(() => {
        if (!responseType) return true
        // Header, Label, and Auto Number cannot have a score
        if (isNonScorableResponseItem(responseType.item)) return true
        if (responseType.type === TemplateResponseType.Single) {
            return false // Enable for single response
        }
        if (
            responseType.type === TemplateResponseType.Multiple &&
            responseType.item?.Items
        ) {
            // Enable only if any option has IsEditAvailable === true
            return !responseType.item.Items.some(opt => opt.IsEditAvailable)
        }
        return true // Default: disabled
    }, [responseType])

    // Clear score when switching to a non-scorable response type.
    // Formula questions are never scored and never required.
    useEffect(() => {
        if (!isNonScorableResponseItem(responseType?.item)) return
        setValue('Score', 0)
        setQuestionScore(0)
        if (isFormulaResponseItem(responseType?.item)) {
            setValue('IsRequired', false)
            setCustomCheckbox(false)
            setNestedQuestions([])
        }
    }, [responseType, setValue])

    const nestedControlOptions = useMemo(
        () => buildNestedControlOptions(responseTypes),
        [responseTypes],
    )

    const showNestedQuestionCheckbox =
        responseType?.type === TemplateResponseType.Single &&
        !!responseType?.item &&
        !isFormulaResponseItem(responseType.item)

    const isFormulaType =
        responseType?.type === TemplateResponseType.Single &&
        isFormulaResponseItem(responseType?.item)

    const formulaQuestionLabel = plainQuestionText(watch('Question'))

    const rememberFormulaDraft = draft => {
        formulaDraftRef.current = draft
        if (!draft) return
        const text =
            draft.source === 'codes' ? draft.codesText : draft.excelText
        setFormulaConfig({
            Source: draft.source,
            ExcelText: text || '',
            CellMap: draft.cellMap || {},
            Decimals: draft.decimals,
            Suffix: draft.suffix,
        })
        setFormulaSummary(formulaSummaryText(draft))
        setFormulaChecking(!!draft.checking)
        if (draft.valid && draft.expr) setFormulaNotice('')
    }

    const openFormulaModal = () => {
        formulaSnapshotRef.current = formulaDraftRef.current
        setFormulaModalOpen(true)
    }

    const applyFormulaSnapshot = snapshot => {
        formulaDraftRef.current = snapshot
        if (!snapshot) {
            setFormulaConfig(null)
            setFormulaSummary('')
            return
        }
        const text =
            snapshot.source === 'codes' ? snapshot.codesText : snapshot.excelText
        setFormulaConfig({
            Source: snapshot.source,
            ExcelText: text || '',
            CellMap: snapshot.cellMap || {},
            Decimals: snapshot.decimals,
            Suffix: snapshot.suffix,
        })
        setFormulaSummary(formulaSummaryText(snapshot))
    }

    const cancelFormulaModal = () => {
        applyFormulaSnapshot(formulaSnapshotRef.current)
        setFormulaModalOpen(false)
    }

    const closeFormulaModal = () => {
        const draft = formulaDraftRef.current
        if (draft?.checking) return
        if (draft?.unmappedMessage && draft?.hasQuestionOptions) {
            setFormulaNotice(draft.unmappedMessage)
            return
        }
        if (draft?.error) {
            setFormulaNotice(draft.error)
            return
        }
        if (!draft?.valid && !draft?.expr) {
            setFormulaNotice('Enter a valid formula before saving.')
            return
        }
        setFormulaNotice('')
        setFormulaModalOpen(false)
    }

    const formulaCatalog = useMemo(() => {
        if (!isFormulaType || !questionGuid) return null
        return buildFormulaCatalog({
            sections: templateSections,
            currentSectionId: sectionId,
            currentQuestions: sectionQuestions,
            sectionName,
            selfLocalId: question?.Id,
            selfGuid: questionGuid,
            selfLabel: formulaQuestionLabel,
            guidCache: guidCacheRef.current,
        })
    }, [
        isFormulaType,
        questionGuid,
        templateSections,
        sectionId,
        sectionQuestions,
        sectionName,
        question?.Id,
        formulaQuestionLabel,
    ])

    useEffect(() => {
        const patches = formulaCatalog?.guidPatches || []
        if (!patches.length || !onStampQuestionGuids) return
        onStampQuestionGuids(patches)
    }, [formulaCatalog, onStampQuestionGuids])

    const rejectFormulaIfNeeded = () => {
        if (!isFormulaType) return false
        const draft = formulaDraftRef.current
        if (draft?.valid && !draft.checking && !draft?.error) return false
        if (draft?.unmappedMessage && !draft?.hasQuestionOptions && !draft?.error) return false
        if (!draft && question?.Formula?.Expr) return false
        if (draft?.unmappedMessage && draft?.hasQuestionOptions && !draft?.error) {
            setError('responseType', {
                type: 'manual',
                message: draft.unmappedMessage,
            })
            setFormulaNotice(draft.unmappedMessage)
            formulaSnapshotRef.current = draft
            setFormulaModalOpen(true)
            return true
        }
        const message = draft?.checking
            ? 'Checking the formula…'
            : draft?.error || 'Enter a valid formula before saving.'
        setError('responseType', {
            type: 'manual',
            message,
        })
        setFormulaNotice(message)
        formulaSnapshotRef.current = draft
        setFormulaModalOpen(true)
        return true
    }

    const withFormulaIdentity = temp => {
        const draft = formulaDraftRef.current
        const next = { ...temp }
        const existingGuid = readQuestionGuid(question)
        const isNewQuestion =
            question?.Id == null ||
            question?.Id === '' ||
            String(question.Id).includes('.')
        if (isFormulaType && (draft?.expr || draft?.valid)) {
            next.QuestionId = existingGuid || draft.questionId || questionGuid
            next.Score = 0
            next.IsRequired = false
            next.IsNestedQuestion = false
            next.NestedOrder = 0
            next.ParentId = ''
            next.ChildQuestions = []
            next.Formula = {
                Expr: draft.expr,
                Decimals: draft.decimals,
                Suffix: draft.suffix || '',
                Source: draft.source === 'codes' ? 'codes' : 'excel',
                ExcelText:
                    draft.source === 'codes' ? draft.codesText || '' : draft.excelText || '',
                CellMap: draft.source === 'excel' ? draft.cellMap || {} : {},
            }
        } else if (isNewQuestion) {
            next.QuestionId = existingGuid || questionGuid
        } else if (existingGuid) {
            next.QuestionId = existingGuid
        }
        if (!isFormulaType && (isFormulaQuestion(question) || question?.Formula)) {
            next.Formula = null
        }
        if (next.QuestionDisplaySetting) {
            next.QuestionDisplaySetting = {
                ...next.QuestionDisplaySetting,
                QuestionId: numericDisplayQuestionId(
                    next.QuestionDisplaySetting,
                    question,
                ),
            }
        }
        return next
    }

    const { nestedQuestionsSummary, nestedQuestionsDetail } = useMemo(() => {
        if (!nestedQuestions?.length) {
            return { nestedQuestionsSummary: '', nestedQuestionsDetail: '' }
        }
        const detailLines = nestedQuestions.map((n, idx) => {
            const raw = (n.Question || '').trim()
            const plain = raw.startsWith('<')
                ? CommonUtility.strippedHtml(raw).trim()
                : raw
            const qDisp = plain || '(empty question)'
            const answer = findNestedControlOption(nestedControlOptions, n)
            const answerLabel = answer?.text ? ` — ${answer.text}` : ''
            return `${idx + 1}. ${qDisp}${answerLabel}`
        })
        const detail = detailLines.join('\n\n')
        const first = detailLines[0] || ''
        const maxChars = 22
        let summary = first
        if (first.length > maxChars) {
            const visibleLen = Math.max(0, maxChars - 3)
            summary = `${first.slice(0, visibleLen)}...`
        }
        if (nestedQuestions.length > 1) {
            summary += ` (+${nestedQuestions.length - 1})`
        }
        return {
            nestedQuestionsSummary: summary,
            nestedQuestionsDetail: detail,
        }
    }, [nestedQuestions, nestedControlOptions])

    const handleNestedQuestionsSave = nodes => {
        setNestedQuestions(nodes)
        setIsNestedModalOpen(false)
        if (!nodes.length) setCustomCheckbox(false)
    }

    const handleNestedQuestionsDismiss = () => {
        setIsNestedModalOpen(false)
        if (nestedQuestions.length === 0) setCustomCheckbox(false)
    }

    const watchedQuestionHtml = watch('Question')
    const parentQuestionPlain = useMemo(
        () =>
            CommonUtility.strippedHtml(watchedQuestionHtml || '')
                .trim() || '—',
        [watchedQuestionHtml],
    )

    const parentResponseLabel =
        (responseType?.item?.DisplayText || '').trim() || '—'

    return (
        <Modal open={open} onClose={() => onClose()}>
            <Modal.Header>
                <FlexRowBetween>
                    <PageHeader
                        header={`${editMode ? 'Edit' : 'Create'} Questions`}
                    />
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onClose()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form noValidate>
                    {/* Action buttons moved above Question field */}
                    <div className="d-flex justify-content-end mb-3">
                        {!editMode && (
                            <AppPrimaryButton
                                type="button"
                                onClick={handleSubmit(handleNext)}
                                className="me-2"
                                style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                            >
                                Create Next Question
                            </AppPrimaryButton>
                        )}
                        <AppPrimaryButton
                            type="button"
                            onClick={handleSubmit(submit)}
                        >
                            Submit
                        </AppPrimaryButton>
                    </div>
                    <RTEModal>
                        <RTEFormField
                            control={control}
                            defaultValue=""
                            label="Question"
                            name="Question"
                            errors={errors?.Question}
                        />
                    </RTEModal>
                    <Form.Group className="align-items-center mt-2 justify-content-between">
                        <Form.Field
                            className="d-flex justify-content-end"
                            width={5}
                        >
                            <FormTextFormField
                                control={control}
                                label="Score"
                                name="Score"
                                defaultValue={0}
                                errors={errors?.Score}
                                onChange={handleScoreChange}
                                disabled={isScoreDisabled}
                            />
                        </Form.Field>
                    </Form.Group>
                    <Form.Group className="align-items-center mt-2 justify-content-between">
                        <Form.Field className="d-flex justify-content-end">
                            <FormColorFormField
                                control={control}
                                name="ColorCode"
                                label="Color"
                                defaultValue={undefined}
                            />
                        </Form.Field>
                        <Form.Field className="d-flex justify-content-end">
                            <FormToggleFormField
                                control={control}
                                label="Is Required"
                                name="IsRequired"
                                defaultValue={false}
                                disabled={isFormulaType}
                            />
                        </Form.Field>
                        <Form.Field className="d-flex justify-content-end">
                            <FormToggleFormField
                                control={control}
                                label="Show Media"
                                name="QuestionDisplaySetting.IsMediaAllowed"
                            />
                        </Form.Field>
                        <Form.Field className="d-flex justify-content-end">
                            <FormToggleFormField
                                control={control}
                                label="Show Notes"
                                name="QuestionDisplaySetting.IsNoteAllowed"
                            />
                        </Form.Field>
                        {user?.CompanyId === 34930 && <Form.Field className="d-flex justify-content-end">
                            <FormToggleFormField
                                control={control}
                                label="Show NC Question"
                                name="QuestionDisplaySetting.ShowNCQuestion"
                                defaultValue={false}
                            />
                        </Form.Field>}
                        <Form.Field className="d-flex justify-content-end col mt-4">
                            <AppPrimaryButton
                                onClick={() => onpenAdditionalInfoClose()}
                            >
                                Add Addition Info
                            </AppPrimaryButton>
                        </Form.Field>
                    </Form.Group>
                    <FlexRowBetween className="my-3 align-items-start">
                        <div className="d-flex flex-column pe-2">
                            <label className="mb-0">Response Type</label>
                            {errors?.responseType?.message && (
                                <div
                                    className="mt-1"
                                    style={{ color: '#9f3a38', fontSize: '0.9rem' }}
                                >
                                    {errors.responseType.message}
                                </div>
                            )}
                            {showNestedQuestionCheckbox && (
                                <div className="mt-2">
                                    <Checkbox
                                        label="Nested Question"
                                        checked={customCheckbox}
                                        onChange={(_, data) => {
                                            const checked = !!data.checked
                                            setCustomCheckbox(checked)
                                            if (checked) {
                                                setIsNestedModalOpen(true)
                                            } else {
                                                setNestedQuestions([])
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div
                            className="d-flex flex-column align-items-end flex-grow-1"
                            style={{ minWidth: 0, gap: 10 }}
                        >
                            {responseType?.type && responseType?.item && (
                                <div
                                    style={{
                                        alignSelf: 'flex-end',
                                        maxWidth: '100%',
                                    }}
                                >
                                    <ResponseTag
                                        item={responseType.item}
                                        type={responseType.type}
                                        question={watch('Question')}
                                        existingData={parentChildResponse}
                                        existingAutoNumberData={
                                            autoNumberConfig
                                        }
                                        onAutoNumberDataReceived={
                                            handleAutoNumberDataReceived
                                        }
                                        onFormulaOpen={openFormulaModal}
                                        formulaSummary={
                                            formulaSummary ||
                                            (isFormulaType
                                                ? 'Set formula'
                                                : '')
                                        }
                                        cornerLayout
                                        nestedSummary=""
                                        nestedDetail=""
                                    />
                                </div>
                            )}
                            {showNestedQuestionCheckbox &&
                                customCheckbox && (
                                    <div
                                        className="d-flex flex-column align-items-end"
                                        style={{ gap: 8, maxWidth: '100%' }}
                                    >
                                        {nestedQuestions.length > 0 &&
                                            nestedQuestionsSummary &&
                                            nestedQuestionsDetail && (
                                                <div
                                                    style={{
                                                        textAlign: 'right',
                                                        fontSize: '1rem',
                                                        color: '#555',
                                                    }}
                                                >
                                                    <Popup
                                                        hoverable
                                                        position="bottom right"
                                                        content={
                                                            <pre
                                                                style={{
                                                                    whiteSpace:
                                                                        'pre-wrap',
                                                                    margin: 0,
                                                                    fontFamily:
                                                                        'inherit',
                                                                }}
                                                            >
                                                                {
                                                                    nestedQuestionsDetail
                                                                }
                                                            </pre>
                                                        }
                                                        trigger={
                                                            <span
                                                                onClick={e =>
                                                                    e.stopPropagation()
                                                                }
                                                            >
                                                                <span
                                                                    style={{
                                                                        color: '#14532d',
                                                                        fontWeight: 700,
                                                                        marginRight: 6,
                                                                    }}
                                                                >
                                                                    Nested
                                                                </span>
                                                                <span
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        textDecoration:
                                                                            'underline dotted',
                                                                    }}
                                                                >
                                                                    {
                                                                        nestedQuestionsSummary
                                                                    }
                                                                </span>
                                                            </span>
                                                        }
                                                    />
                                                </div>
                                            )}
                                        {nestedQuestions.length === 0 && (
                                            <span
                                                style={{
                                                    color: '#14532d',
                                                    fontWeight: 700,
                                                    fontSize: '1rem',
                                                    textAlign: 'right',
                                                }}
                                            >
                                                Nested
                                            </span>
                                        )}
                                        <AppPrimaryButton
                                            type="button"
                                            style={{
                                                backgroundColor: '#2841a1',
                                                borderColor: '#2841a1',
                                                fontSize: '0.85rem',
                                                padding:
                                                    '0.4rem 0.75rem',
                                                alignSelf: 'flex-end',
                                            }}
                                            onClick={() =>
                                                setIsNestedModalOpen(true)
                                            }
                                        >
                                            Configure nested questions
                                        </AppPrimaryButton>
                                    </div>
                                )}
                        </div>
                    </FlexRowBetween>
                    <Modal
                        open={formulaModalOpen}
                        onClose={cancelFormulaModal}
                        size="large"
                    >
                        <Modal.Header>
                            <FlexRowBetween>
                                <span>Formula</span>
                                <X
                                    className="pointer"
                                    size={32}
                                    onClick={cancelFormulaModal}
                                />
                            </FlexRowBetween>
                        </Modal.Header>
                        <Modal.Content scrolling>
                            {formulaModalOpen && (
                                <FormulaEditor
                                    key={questionGuid}
                                    templateId={templateId}
                                    questionId={questionGuid}
                                    questionLabel={formulaQuestionLabel}
                                    initialFormula={
                                        formulaConfig || question?.Formula
                                    }
                                    catalog={formulaCatalog}
                                    onChange={rememberFormulaDraft}
                                />
                            )}
                        </Modal.Content>
                        <Modal.Actions>
                            <AppPrimaryButton
                                type="button"
                                style={{
                                    backgroundColor: '#fff',
                                    color: '#2841a1',
                                    border: '1px solid #2841a1',
                                    marginRight: 8,
                                }}
                                onClick={cancelFormulaModal}
                            >
                                Cancel
                            </AppPrimaryButton>
                            <AppPrimaryButton
                                type="button"
                                disabled={formulaChecking}
                                onClick={closeFormulaModal}
                            >
                                {formulaChecking ? 'Checking…' : 'Save'}
                            </AppPrimaryButton>
                        </Modal.Actions>
                        {formulaNotice ? (
                            <div
                                style={{
                                    padding: '0 1rem 1rem',
                                    color: '#9f3a38',
                                    fontSize: '0.9rem',
                                }}
                            >
                                {formulaNotice}
                            </div>
                        ) : null}
                    </Modal>
                    {responseType?.type === TemplateResponseType.Single &&
                        responseType?.item?.Rules &&
                        responseType.item.Rules.length > 0 && (
                            <div className="mt-2 d-flex justify-content-end">
                                <AppPrimaryButton
                                    type="button"
                                    size="small"
                                    onClick={() => setIsRuleModalOpen(true)}
                                >
                                    Set Rule
                                </AppPrimaryButton>
                            </div>
                        )}
                    <SetRuleModal
                        open={isRuleModalOpen}
                        onClose={data => {
                            setIsRuleModalOpen(false)
                            if (data) setValidationRule(data)
                        }}
                        responseItem={responseType?.item}
                        initialRule={validationRule}
                    />
                    <ResponseTypeSelectionModal
                        open={openModal}
                        onClose={handleResponseTypeClose}
                        responseTypes={responseTypes}
                        setCreate={handleCreateMultipleResponse}
                        setEditMultipleResponses={handleEditMultipleResponse}
                        question={watch('Question')}
                        onParentChildDataReceived={
                            handleParentChildDataReceived
                        }
                        onAutoNumberDataReceived={
                            handleAutoNumberDataReceived
                        }
                        existingAutoNumberData={autoNumberConfig}
                    />
                    <CreateResponseModal
                        open={!!createModal}
                        onClose={onCreteClose}
                        responseTypes={responseTypes}
                        editMode={
                            !!(
                                createModal &&
                                createModal.Items &&
                                createModal.Items.length
                            )
                        }
                        templateId={templateId}
                        templateNumericId={templateNumericId}
                        multipleResponcesList={multipleResponcesList}
                        data={createModal}
                        questionScore={questionScore}
                        isOtherOptionAvailable={isOtherOptionAvailable}
                        initialMinAnswer={(() => {
                            const groupId = createModal?.Items?.[0]?.GroupId
                            if (
                                groupId != null &&
                                listBoxLimitsByGroupId[groupId] != null
                            ) {
                                return listBoxLimitsByGroupId[groupId].MinAnswer
                            }
                            if (!createModal?.Items?.length) {
                                return 0
                            }
                            return (
                                question?.MinAnswer ??
                                watch('MinAnswer') ??
                                0
                            )
                        })()}
                        initialMaxAnswer={(() => {
                            const groupId = createModal?.Items?.[0]?.GroupId
                            if (
                                groupId != null &&
                                listBoxLimitsByGroupId[groupId] != null
                            ) {
                                return listBoxLimitsByGroupId[groupId].MaxAnswer
                            }
                            if (!createModal?.Items?.length) {
                                return 1
                            }
                            return (
                                question?.MaxAnswer ??
                                watch('MaxAnswer') ??
                                1
                            )
                        })()}
                        onMinMaxChange={handleMinMaxChange}
                    />
                    <AddAdditionalInfoModal
                        open={!!additionalInfoModal}
                        onClose={onAdditionalInfoClose}
                        data={additionalInfoModalData}
                    />
                    <NestedQuestionConfigModal
                        open={isNestedModalOpen}
                        onDismiss={handleNestedQuestionsDismiss}
                        value={nestedQuestions}
                        onSave={handleNestedQuestionsSave}
                        controlTypeOptions={nestedControlOptions}
                        parentQuestionText={parentQuestionPlain}
                        parentResponseLabel={parentResponseLabel}
                        onCreateCustomGroup={handleCreateMultipleResponse}
                    />
                    {/* Bottom submit intentionally hidden for all viewports per request */}
                </Form>
            </Modal.Content>
        </Modal>
    )
}
